import Image from "next/image";
import { useRouter } from "next/router";
import React from "react";
import InquiryContext from "@/utils/inquiry-context";
import EditProduct from "../inquiry/edit-product";
import { m } from "framer-motion";

const ProductDetails = ({ product }) => {
  const [mainImage, setMainImage] = React.useState(
    product.images[1] ?? product.images[0],
  );
  const hasMultipleImages = product.images.length > 1;
  const router = useRouter();
  const [showButtons, setShowButtons] = React.useState(false);
  React.useEffect(() => {
    setShowButtons(true);
  }, []);

  const { products, addProduct } = React.useContext(InquiryContext);

  const [addedNotification, setAddedNotification] = React.useState(false);
  const addedToInquiry = React.useMemo(
    () => (products.find((p) => p.code === product.code) ? true : false),
    [products],
  );
  const addToInquiry = () => {
    if (addedToInquiry || addedNotification) {
      setEditDialogOpen(true);
      return;
    }
    addProduct({ code: product.code, name: product.name, options: {} });
    setAddedNotification(true);
    setTimeout(() => {
      setAddedNotification(false);
      setEditDialogOpen(true);
    }, 1000);
  };

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  return (
    <section className="flex w-full flex-col gap-8">
      <m.div
        className="card flex w-full flex-col gap-8 p-4 lg:flex-row"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* photos */}
        <div className="flex w-full flex-col items-start gap-4 md:h-[480px] md:w-auto md:flex-row-reverse lg:w-full">
          <div
            className={`w-full bg-auto-black/[.025] p-4  ${hasMultipleImages ? "md:h-full md:w-4/5 lg:w-4/6" : "h-full"}`}
          >
            <div
              className={`relative aspect-square w-full md:aspect-auto md:h-full ${hasMultipleImages ? "" : "!aspect-default !h-full"}`}
            >
              <Image
                src={mainImage}
                alt={product.name}
                fill
                className={`absolute object-center drop-shadow-[10px_5px_7px_rgba(0,0,0,0.4)] ${hasMultipleImages ? "object-contain" : "object-cover"}`}
                sizes="(max-width: 600px) 350px, (max-width: 900px) 350px, (max-width: 1200px) 450px, 250px"
              />
            </div>
          </div>
          <div
            className={`flex w-full flex-row items-start gap-2 md:h-full md:w-1/5 md:flex-col md:items-end lg:w-2/6 ${hasMultipleImages ? "" : "hidden"}`}
          >
            {product.images.slice(1).map((image, index) => (
              <div
                className="aspect-square flex-auto bg-auto-black/[.025] p-2 md:max-h-28"
                key={index}
                onClick={() => {
                  setMainImage(image);
                }}
              >
                <div className="relative aspect-square md:h-full">
                  <Image
                    src={image}
                    alt={product.name}
                    fill
                    className="object-contain object-center drop-shadow-[5px_3px_4px_rgba(0,0,0,0.3)]"
                    sizes="(max-width: 600px) 50px, (max-width: 900px) 150px, (max-width: 1200px) 150px, 75px"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* specs and inquiry buttons */}
        <div className="flex w-full flex-col items-start justify-start gap-2">
          {product.details.map((detail, index) => (
            <ProductSpec key={index} detail={detail} />
          ))}
          {product.options.map((option, index) => {
            const description = option.values.join(" | ");
            return (
              <ProductSpec
                key={index}
                detail={{ title: option.title, description }}
              />
            );
          })}
          {product.specs.map((spec, index) => (
            <ProductSpec key={index} detail={spec} />
          ))}
          <div
            className={`flex h-20 w-full flex-row items-center justify-start ${addedToInquiry && !addedNotification ? "gap-4" : ""} pt-8`}
          >
            {showButtons && (
              <>
                <button
                  className={`${!addedToInquiry || addedNotification ? "button grow" : "button-status-success w-12"} transition-all`}
                  onClick={addToInquiry}
                  suppressHydrationWarning
                >
                  {addedNotification
                    ? "Added"
                    : addedToInquiry
                      ? ""
                      : "Add to Inquiry"}
                  {!addedToInquiry && !addedNotification ? (
                    <svg
                      key="add"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-6 w-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                  ) : (
                    <svg
                      key="check"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="h-8 w-8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                  )}
                </button>
                <button
                  className={`${!addedNotification && addedToInquiry ? "button grow" : "button-invisible"} transition-all `}
                  onClick={() => {
                    router.push("/inquiry");
                  }}
                >
                  Proceed to Inquiry
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </m.div>
      <m.div
        className="card flex w-full flex-col gap-4 p-4"
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="text-2xl font-bold text-auto-black/70">Description</div>
        <div className="h-px w-full bg-auto-black/20"></div>
        <div className="flex w-full flex-col items-start gap-4">
          {product.description}
        </div>
      </m.div>
      <EditProduct
        open={editDialogOpen}
        setOpen={setEditDialogOpen}
        product={product}
      />
    </section>
  );
};

export default ProductDetails;

const ProductSpec = ({ detail }) => {
  return (
    <div className="text-auto-black/60">
      <span className="font-bold text-auto-black/70">
        {detail.title}:&nbsp;
      </span>
      {detail.description}
    </div>
  );
};
